import React, { useEffect, useState } from "react";
import { getApiCall } from "../../service/httpCall";
import { API_SYSTEM_MONITORING_STATS } from "../../service/url";
import { USER_ROLES } from "../../utilities/constant";
import Loader from "../../components/partials/components/loader";
import { toast } from "react-toastify";
import AsyncStorage from "@react-native-async-storage/async-storage";

import {
  FaServer,
  FaMemory,
  FaHdd,
  FaWifi,
  FaExclamationTriangle,
  FaCheckCircle,
  FaNetworkWired,
} from "react-icons/fa";
import { BsCpuFill, BsHddFill } from "react-icons/bs";

import { Card, Row, Col, Badge, ProgressBar } from "react-bootstrap";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";

/* ─── Helpers ────────────────────────────────────────────────────────────── */
const maskValue = (value) => {
  if (!value) return "";
  const str = value.toString();
  if (str.length <= 3) return str;
  return "*".repeat(str.length - 3) + str.slice(-3);
};

const formatUptime = (seconds) => {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const parts = [];
  if (d > 0) parts.push(`${d}d`);
  if (h > 0) parts.push(`${h}h`);
  parts.push(`${m}m`);
  return parts.join(" ");
};

const fmtMB = (mb) => {
  const n = parseFloat(mb || 0);
  if (n >= 1024) return `${(n / 1024).toFixed(2)} GB`;
  return `${n.toFixed(0)} MB`;
};

const barColor = (pct) => {
  if (pct >= 85) return "#f43f5e";
  if (pct >= 70) return "#f59e0b";
  return "#10b981";
};

/* ─── Stat card ──────────────────────────────────────────────────────────── */
const StatCard = ({
  icon,
  label,
  value,
  unit,
  sub,
  progress,
  progressVariant,
  statusIcon,
  statusLabel,
  accentColor,
}) => (
  <div
    style={{
      background: "var(--dash-surface)",
      border: "1px solid var(--dash-border)",
      borderRadius: "var(--dash-radius)",
      boxShadow: "var(--dash-shadow)",
      padding: "18px 20px",
      display: "flex",
      flexDirection: "column",
      gap: 10,
      height: "100%",
      transition: "transform 0.2s, box-shadow 0.2s",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = "translateY(-3px)";
      e.currentTarget.style.boxShadow = "0 8px 32px rgba(0,0,0,0.15)";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.boxShadow = "var(--dash-shadow)";
    }}
  >
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
      }}
    >
      <div>
        <div
          style={{
            fontSize: 12,
            color: "var(--dash-text-soft)",
            fontWeight: 600,
            marginBottom: 4,
          }}
        >
          {label}
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
          <span
            style={{
              fontSize: 32,
              fontWeight: 800,
              color: "var(--dash-text)",
              lineHeight: 1,
            }}
          >
            {value}
          </span>
          {unit && (
            <span
              style={{
                fontSize: 15,
                color: "var(--dash-text-soft)",
                fontWeight: 500,
              }}
            >
              {unit}
            </span>
          )}
        </div>
        {sub && (
          <div
            style={{
              fontSize: 11,
              color: "var(--dash-text-soft)",
              marginTop: 2,
            }}
          >
            {sub}
          </div>
        )}
      </div>
      <div
        style={{
          width: 46,
          height: 46,
          borderRadius: 14,
          background: accentColor
            ? `${accentColor}18`
            : "var(--dash-primary-soft)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: accentColor || "var(--dash-primary)",
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
    </div>

    {progress !== undefined && (
      <div>
        <ProgressBar
          now={progress}
          variant={progressVariant}
          className="rounded-pill"
          style={{ height: 6, background: "var(--dash-border)" }}
        />
      </div>
    )}

    {(statusIcon !== undefined || statusLabel) && (
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        {statusIcon}
        <span style={{ fontSize: 11, fontWeight: 600 }}>{statusLabel}</span>
      </div>
    )}
  </div>
);

/* ─── Service card ───────────────────────────────────────────────────────── */
const ServiceCard = ({ name, service, bars = [], rows = [] }) => {
  const isConnected = service?.status === "connected";
  return (
    <div
      className="service-card"
      style={{ display: "flex", flexDirection: "column", gap: 8 }}
    >
      {/* Name + status */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <span style={{ fontWeight: 700, fontSize: 13, color: "var(--dash-text)" }}>
          {name}
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <span className="status-dot" style={{ background: isConnected ? "#10b981" : "#f43f5e" }} />
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: isConnected ? "#10b981" : "#f43f5e" }}>
            {service?.status || "unknown"}
          </span>
        </span>
      </div>

      {/* Latency + host */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {service?.latencyMs !== undefined && (
          <div style={{ fontSize: 11, color: "var(--dash-text-soft)" }}>
            <span style={{ fontWeight: 700, color: "var(--dash-text)" }}>{service.latencyMs}</span> ms
          </div>
        )}
        {service?.host && (
          <div style={{ fontSize: 10, color: "var(--dash-text-soft)" }}>
            {maskValue(service.host)}
          </div>
        )}
      </div>

      {/* Progress bars */}
      {bars.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 7, marginTop: 2 }}>
          {bars.map(({ label, pct, meta, color }) => {
            const clampedPct = Math.min(Math.max(pct || 0, 0), 100);
            const c = color || barColor(clampedPct);
            return (
              <div key={label}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: "var(--dash-text-soft)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    {label}
                  </span>
                  <span style={{ fontSize: 10, fontWeight: 700, color: c }}>
                    {meta}
                  </span>
                </div>
                <div style={{ height: 5, borderRadius: 999, background: "var(--dash-border)", overflow: "hidden" }}>
                  <div
                    style={{
                      height: "100%",
                      width: `${clampedPct}%`,
                      background: c,
                      borderRadius: 999,
                      transition: "width 0.4s ease",
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Key-value rows */}
      {rows.length > 0 && (
        <div
          style={{
            marginTop: 2,
            padding: "6px 8px",
            background: "var(--dash-bg, #f8fafc)",
            borderRadius: 8,
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          {rows.map(({ label, value }) => (
            <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 10, fontWeight: 600, color: "var(--dash-text-soft)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                {label}
              </span>
              <span style={{ fontSize: 11, fontWeight: 700, color: "var(--dash-text)" }}>
                {value ?? "—"}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   SystemMonitor
═══════════════════════════════════════════════════════════════════════════ */
const SystemMonitor = () => {
  const [systemData, setSystemData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loggedInUserRole, setLoggedInUserRole] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    const checkUserRole = async () => {
      const rawUser = await AsyncStorage.getItem("user");
      if (!rawUser) return;
      try {
        const user = JSON.parse(rawUser);
        if (user.role) setLoggedInUserRole(user.role);
      } catch (e) {
        console.error("Invalid user data", e);
      }
    };
    checkUserRole();
  }, []);

  useEffect(() => {
    if (!loggedInUserRole || loggedInUserRole !== USER_ROLES.SUPERADMIN) return;

    const fetchSystemStats = async () => {
      try {
        const resp = await getApiCall(API_SYSTEM_MONITORING_STATS, {});

        setSystemData(resp);

        setHistory((prev) => [
          ...prev.slice(-19),
          {
            time: new Date().toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            }),
            cpu: parseFloat(resp.system?.cpuLoadPercent || 0),
            memory: parseFloat(resp.system?.usagePercent || 0),
          },
        ]);
      } catch (err) {
        toast.error("Failed to fetch system stats");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchSystemStats();

    const interval = setInterval(fetchSystemStats, 30000);
    return () => clearInterval(interval);
  }, [loggedInUserRole]);

  if (loading || !systemData) return <Loader />;

  /* ── Destructure API response ─────────────────────────────────────────── */
  const sys = systemData.system || {};
  const disk = systemData.disk || {};
  const pm2Processes = systemData.pm2 || [];
  const services = systemData.services || {};
  const network = sys.network || { downloadMbps: "0.00", uploadMbps: "0.00" };
  const connections = sys.connections || {
    established: 0,
    http: 0,
    websocket: 0,
    total: 0,
  };

  const memoryUsedPercent = parseFloat(sys.usagePercent || 0);
  const cpuUsedPercent = parseFloat(sys.cpuLoadPercent || 0);
  const diskUsedPercent = parseFloat(disk.usagePercent || 0);

  const getStatusColor = (percent) => {
    if (percent > 80) return "danger";
    if (percent > 70) return "warning";
    return "success";
  };

  const getStatusIcon = (percent) =>
    percent > 80 ? (
      <FaExclamationTriangle className="text-danger" />
    ) : (
      <FaCheckCircle className="text-success" />
    );

  const getStatusLabel = (percent) => {
    if (percent > 80) return "CRITICAL";
    if (percent > 70) return "HIGH";
    return "NORMAL";
  };

  const memoryData = [
    { name: "Used", value: memoryUsedPercent, fill: "#6366f1" },
    { name: "Free", value: 100 - memoryUsedPercent, fill: "#10b981" },
  ];

  /* PrimeReact templates (kept — used if pm2 section ever re-enabled) */
  const statusBodyTemplate = (rowData) => (
    <Badge
      bg={rowData.status === "online" ? "success" : "danger"}
      pill
      style={{ textTransform: "capitalize", fontWeight: "800" }}
    >
      {rowData.status.toUpperCase()}
    </Badge>
  );
  const cpuBodyTemplate = (rowData) => (
    <span className={rowData.cpu > 50 ? "text-danger fw-bold" : ""}>
      {rowData.cpu}%
    </span>
  );
  const memoryBodyTemplate = (rowData) =>
    `${parseFloat(rowData.memoryInMB || 0).toFixed(1)} MB`;

  /* ─── Shared card style ─────────────────────────────────────────────── */
  const cardStyle = {
    background: "var(--dash-surface)",
    border: "1px solid var(--dash-border)",
    borderRadius: "var(--dash-radius)",
    boxShadow: "var(--dash-shadow)",
  };

  const sectionLabel = {
    fontSize: 11,
    fontWeight: 700,
    color: "var(--dash-text-soft)",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    marginBottom: 4,
  };

  return (
    <div style={{ marginTop: 40 }}>
      {/* ── Page header ─────────────────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
          marginTop: 16,
        }}
      >
        <h5
          style={{
            margin: 0,
            fontWeight: 800,
            fontSize: 18,
            color: "var(--dash-text)",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <FaServer style={{ color: "var(--dash-primary)" }} />
          System Monitor
        </h5>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 12,
            color: "var(--dash-text-soft)",
            background: "var(--dash-surface)",
            border: "1px solid var(--dash-border)",
            borderRadius: 999,
            padding: "4px 14px",
          }}
        >
          <span
            style={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: "#10b981",
              display: "inline-block",
              animation: "pulse 2s infinite",
            }}
          />
          Live &bull; Updates every 30s
        </div>
      </div>

      {/* ══ Row 1: Stat cards ════════════════════════════════════════════════ */}
      <Row className="g-3 mb-3">
        <Col lg={3} md={6}>
          <StatCard
            icon={<BsCpuFill size={22} />}
            label="CPU Usage"
            value={cpuUsedPercent.toFixed(1)}
            unit="%"
            sub={`${sys.cpuCores || "—"} cores`}
            progress={cpuUsedPercent}
            progressVariant={getStatusColor(cpuUsedPercent)}
            statusIcon={getStatusIcon(cpuUsedPercent)}
            statusLabel={getStatusLabel(cpuUsedPercent)}
            accentColor="#0ea5e9"
          />
        </Col>

        <Col lg={3} md={6}>
          <StatCard
            icon={<FaMemory size={22} />}
            label="Memory Usage"
            value={memoryUsedPercent.toFixed(1)}
            unit="%"
            sub={`${parseFloat(sys.usedMemInMB || 0).toFixed(0)} / ${parseFloat(sys.totalMemInMB || 0).toFixed(0)} MB`}
            progress={memoryUsedPercent}
            progressVariant={getStatusColor(memoryUsedPercent)}
            statusIcon={getStatusIcon(memoryUsedPercent)}
            statusLabel={
              memoryUsedPercent > 80 ? "CRITICAL — LOW MEMORY" : "OK"
            }
            accentColor="#f59e0b"
          />
        </Col>

        <Col lg={3} md={6}>
          <StatCard
            icon={<FaHdd size={22} />}
            label="Disk Usage"
            value={diskUsedPercent.toFixed(1)}
            unit="%"
            sub={`${disk.usedGB || "—"} / ${disk.totalGB || "—"} GB`}
            progress={diskUsedPercent}
            progressVariant={getStatusColor(diskUsedPercent)}
            statusIcon={getStatusIcon(diskUsedPercent)}
            statusLabel={diskUsedPercent > 80 ? "CRITICAL — LOW SPACE" : "OK"}
            accentColor="#10b981"
          />
        </Col>

        <Col lg={3} md={6}>
          <div
            style={{
              ...cardStyle,
              padding: "18px 20px",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 10,
              transition: "transform 0.2s, box-shadow 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-3px)";
              e.currentTarget.style.boxShadow = "0 8px 32px rgba(0,0,0,0.15)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "var(--dash-shadow)";
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  color: "var(--dash-text-soft)",
                  fontWeight: 600,
                }}
              >
                Network Throughput
              </div>
              <div
                style={{
                  width: 46,
                  height: 46,
                  borderRadius: 14,
                  background: "#6366f118",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#6366f1",
                }}
              >
                <FaNetworkWired size={22} />
              </div>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 6,
                marginTop: "-20px",
              }}
            >
              <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
                <span
                  style={{ fontSize: 13, color: "#10b981", fontWeight: 700 }}
                >
                  ↓
                </span>
                <span
                  style={{
                    fontSize: 24,
                    fontWeight: 800,
                    color: "var(--dash-text)",
                  }}
                >
                  {parseFloat(network.downloadMbps).toFixed(1)}
                </span>
                <span style={{ fontSize: 12, color: "var(--dash-text-soft)" }}>
                  Mbps
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
                <span
                  style={{ fontSize: 13, color: "#0ea5e9", fontWeight: 700 }}
                >
                  ↑
                </span>
                <span
                  style={{
                    fontSize: 24,
                    fontWeight: 800,
                    color: "var(--dash-text)",
                  }}
                >
                  {parseFloat(network.uploadMbps).toFixed(1)}
                </span>
                <span style={{ fontSize: 12, color: "var(--dash-text-soft)" }}>
                  Mbps
                </span>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <FaWifi style={{ color: "var(--dash-primary)", fontSize: 12 }} />
              <span
                style={{
                  fontSize: 11,
                  color: "var(--dash-text-soft)",
                  fontWeight: 500,
                }}
              >
                {connections.total} active connections
              </span>
            </div>
          </div>
        </Col>
      </Row>

      {/* ══ Row 2: Uptime + Connections + CPU Cores ══════════════════════════ */}
      <Row className="g-3 mb-3">
        {/* Uptime card */}
        <Col lg={4} md={6}>
          <div style={{ ...cardStyle, padding: "18px 20px", height: "100%" }}>
            <div style={sectionLabel}>System Uptime</div>
            <div
              style={{
                fontSize: 30,
                fontWeight: 800,
                color: "var(--dash-text)",
                marginBottom: 4,
              }}
            >
              {formatUptime(sys.uptimeInSeconds || 0)}
            </div>
            <div style={{ fontSize: 12, color: "var(--dash-text-soft)" }}>
              {parseFloat(sys.uptimeInSeconds || 0).toFixed(0)} seconds total
            </div>
          </div>
        </Col>

        {/* Connections breakdown */}
        <Col lg={4} md={6}>
          <div style={{ ...cardStyle, padding: "18px 20px", height: "100%" }}>
            <div style={sectionLabel}>Active Connections</div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 10,
                marginTop: 4,
              }}
            >
              {[
                { label: "Total", value: connections.total, color: "#6366f1" },
                {
                  label: "Established",
                  value: connections.established,
                  color: "#10b981",
                },
                { label: "HTTP", value: connections.http, color: "#f97316" },
                {
                  label: "WebSocket",
                  value: connections.websocket,
                  color: "#0ea5e9",
                },
              ].map(({ label, value, color }) => (
                <div
                  key={label}
                  style={{
                    background: "var(--dash-surface-alt, var(--dash-bg))",
                    borderRadius: 10,
                    padding: "8px 12px",
                    border: "1px solid var(--dash-border)",
                  }}
                >
                  <div
                    style={{
                      fontSize: 10,
                      color: "var(--dash-text-soft)",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    {label}
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 800, color }}>
                    {value ?? "—"}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Col>

        {/* CPU cores info */}
        <Col lg={4} md={6}>
          <div style={{ ...cardStyle, padding: "18px 20px", height: "100%" }}>
            <div style={sectionLabel}>CPU Cores</div>
            <div
              style={{
                fontSize: 48,
                fontWeight: 900,
                color: "var(--dash-primary)",
                lineHeight: 1,
              }}
            >
              {sys.cpuCores || "—"}
            </div>
            <div
              style={{
                fontSize: 12,
                color: "var(--dash-text-soft)",
                marginTop: 4,
              }}
            >
              Logical processors
            </div>
            <div
              style={{
                marginTop: 12,
                display: "flex",
                flexWrap: "wrap",
                gap: 4,
              }}
            >
              {Array.from({ length: sys.cpuCores || 0 }).map((_, i) => (
                <div
                  key={i}
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: 4,
                    background:
                      cpuUsedPercent > 80
                        ? "#f43f5e"
                        : cpuUsedPercent > 70
                          ? "#f59e0b"
                          : "#10b981",
                    opacity: 0.7,
                  }}
                />
              ))}
            </div>
          </div>
        </Col>
      </Row>

      {/* ══ Row 3: Line chart (trend) ═════════════════════════════════════════ */}
      <Row className="g-3 mb-3">
        <Col lg={12}>
          <div style={{ ...cardStyle, padding: "18px 20px" }}>
            <div
              style={{
                fontWeight: 700,
                fontSize: 15,
                color: "var(--dash-text)",
                marginBottom: 12,
              }}
            >
              Resource Usage Trend
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: "var(--dash-text-soft)",
                  marginLeft: 8,
                }}
              >
                Last 20 samples
              </span>
            </div>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart
                data={history}
                margin={{ top: 4, right: 20, left: -10, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="var(--dash-border)"
                />
                <XAxis
                  dataKey="time"
                  tick={{ fontSize: 11, fill: "var(--dash-text-soft)" }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  domain={[0, 100]}
                  tick={{ fontSize: 11, fill: "var(--dash-text-soft)" }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={{
                    background: "var(--dash-surface)",
                    border: "1px solid var(--dash-border)",
                    borderRadius: 10,
                    color: "var(--dash-text)",
                    fontSize: 12,
                  }}
                />
                <Line
                  type="natural"
                  dataKey="cpu"
                  stroke="#0ea5e9"
                  strokeWidth={2.5}
                  dot={false}
                  name="CPU %"
                />
                <Line
                  type="natural"
                  dataKey="memory"
                  stroke="#f59e0b"
                  strokeWidth={2.5}
                  dot={false}
                  name="Memory %"
                />
                <Legend
                  verticalAlign="bottom"
                  height={28}
                  iconType="circle"
                  iconSize={9}
                  wrapperStyle={{
                    fontSize: 12,
                    color: "var(--dash-text-soft)",
                  }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Col>
      </Row>

      {/* ══ Row 4: PM2 Processes ═════════════════════════════════════════════ */}
      {/* {pm2Processes.length > 0 && (
        <div style={{ marginBottom: 30 }}>
          <div
            style={{
              fontWeight: 700,
              fontSize: 15,
              color: "var(--dash-text)",
              marginBottom: 12,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <BsHddFill style={{ color: "var(--dash-primary)" }} />
            Processes
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
              gap: 12,
            }}
          >
            {pm2Processes.map((proc) => (
              <div
                key={proc.name}
                style={{
                  ...cardStyle,
                  padding: "14px 16px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      fontWeight: 700,
                      fontSize: 13,
                      color: "var(--dash-text)",
                    }}
                  >
                    {maskValue(proc.name)}
                  </span>
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      color: proc.status === "online" ? "#10b981" : "#f43f5e",
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    <span
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        background:
                          proc.status === "online" ? "#10b981" : "#f43f5e",
                        display: "inline-block",
                      }}
                    />
                    {proc.status}
                  </span>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gap: 6,
                  }}
                >
                  {[
                    {
                      label: "CPU",
                      value: `${proc.cpu}%`,
                      color: proc.cpu > 50 ? "#f43f5e" : "var(--dash-text)",
                    },
                    {
                      label: "Memory",
                      value: `${parseFloat(proc.memoryInMB || 0).toFixed(0)}MB`,
                      color: "var(--dash-text)",
                    },
                    {
                      label: "Restarts",
                      value: proc.restarts,
                      color: proc.restarts > 5 ? "#f59e0b" : "var(--dash-text)",
                    },
                  ].map(({ label, value, color }) => (
                    <div
                      key={label}
                      style={{
                        background: "var(--dash-surface-alt, var(--dash-bg))",
                        border: "1px solid var(--dash-border)",
                        borderRadius: 8,
                        padding: "5px 6px",
                        textAlign: "center",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 9,
                          color: "var(--dash-text-soft)",
                          fontWeight: 600,
                          textTransform: "uppercase",
                        }}
                      >
                        {label}
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 800, color }}>
                        {value}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )} */}

      {/* ══ Row 5: Services grid ══════════════════════════════════════════════ */}
      <div style={{ marginBottom: "30px", marginTop: "30px" }}>
        <div
          style={{
            fontWeight: 700,
            fontSize: 15,
            color: "var(--dash-text)",
            marginBottom: 12,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <FaServer style={{ color: "var(--dash-primary)" }} />
          Service Health
        </div>

        <div className="services-grid">

          {/* DB Master */}
          {(() => {
            const db = services.dbMaster || {};
            const pool = db.pool || {};
            const disk = db.disk || {};
            const poolPct  = pool.max  ? (pool.used  / pool.max)  * 100 : 0;
            const diskPct  = parseFloat(disk.totalGB)  ? (parseFloat(disk.usedGB)  / parseFloat(disk.totalGB))  * 100 : 0;
            return (
              <ServiceCard
                name="DB Master"
                service={services.dbMaster}
                bars={[
                  { label: "Pool",  pct: poolPct, meta: `${pool.used ?? "—"} / ${pool.max ?? "—"}` },
                  { label: "Disk",  pct: diskPct, meta: `${disk.usedGB ?? "—"} / ${disk.totalGB ?? "—"} GB` },
                ]}
                rows={[
                  { label: "Mem RSS",     value: fmtMB(db.memRssMB) },
                  { label: "DB Size",     value: fmtMB(db.dbSizeMB) },
                  { label: "CPU User",    value: `${db.cpuUserPercent ?? "—"}%` },
                  { label: "CPU Sys",     value: `${db.cpuSysPercent ?? "—"}%` },
                  { label: "Active Conn", value: db.activeConnections ?? "—" },
                ]}
              />
            );
          })()}

          {/* DB Slave */}
          {(() => {
            const db = services.dbSlave || {};
            const pool = db.pool || {};
            const disk = db.disk || {};
            const poolPct  = pool.max  ? (pool.used  / pool.max)  * 100 : 0;
            const diskPct  = parseFloat(disk.totalGB)  ? (parseFloat(disk.usedGB)  / parseFloat(disk.totalGB))  * 100 : 0;
            return (
              <ServiceCard
                name="DB Slave"
                service={services.dbSlave}
                bars={[
                  { label: "Pool",  pct: poolPct, meta: `${pool.used ?? "—"} / ${pool.max ?? "—"}` },
                  { label: "Disk",  pct: diskPct, meta: `${disk.usedGB ?? "—"} / ${disk.totalGB ?? "—"} GB` },
                ]}
                rows={[
                  { label: "Mem RSS",     value: fmtMB(db.memRssMB) },
                  { label: "DB Size",     value: fmtMB(db.dbSizeMB) },
                  { label: "CPU User",    value: `${db.cpuUserPercent ?? "—"}%` },
                  { label: "CPU Sys",     value: `${db.cpuSysPercent ?? "—"}%` },
                  { label: "Active Conn", value: db.activeConnections ?? "—" },
                ]}
              />
            );
          })()}

          {/* Asterisk */}
          {(() => {
            const svc = services.asterisk || {};
            return (
              <ServiceCard
                name="Asterisk"
                service={services.asterisk}
                bars={[]}
                rows={[
                  { label: "Threads",     value: `${svc.threadsConnected ?? "—"} conn / ${svc.threadsRunning ?? "—"} run` },
                  // { label: "Queries",     value: (svc.totalQueries || 0).toLocaleString() },
                  { label: "Uptime",      value: formatUptime(svc.uptimeSeconds || 0) },
                  { label: "Received",    value: fmtMB(svc.bytesReceivedMB) },
                  { label: "Sent",        value: fmtMB(svc.bytesSentMB) },
                  { label: "Buffer Pool", value: fmtMB(svc.innodbBufferPoolMB) },
                ]}
              />
            );
          })()}

          {/* Kamailio */}
          {(() => {
            const svc = services.kamailio || {};
            return (
              <ServiceCard
                name="Kamailio"
                service={services.kamailio}
                bars={[]}
                rows={[
                  { label: "Threads",     value: `${svc.threadsConnected ?? "—"} conn / ${svc.threadsRunning ?? "—"} run` },
                  // { label: "Queries",     value: (svc.totalQueries || 0).toLocaleString() },
                  { label: "Uptime",      value: formatUptime(svc.uptimeSeconds || 0) },
                  { label: "Received",    value: fmtMB(svc.bytesReceivedMB) },
                  { label: "Sent",        value: fmtMB(svc.bytesSentMB) },
                  { label: "Buffer Pool", value: fmtMB(svc.innodbBufferPoolMB) },
                ]}
              />
            );
          })()}

          {/* ClickHouse */}
          {(() => {
            const svc  = services.clickhouse || {};
            const load = svc.load || {};
            const ram  = svc.ram  || {};
            const disk = svc.disk || {};
            const diskPct = parseFloat(disk.usagePercent || 0);
            return (
              <ServiceCard
                name="ClickHouse"
                service={services.clickhouse}
                bars={[
                  { label: "Disk", pct: diskPct, meta: `${disk.usedGB ?? "—"} / ${disk.totalGB ?? "—"} GB` },
                ]}
                rows={[
                  { label: "Active Queries", value: load.activeQueries ?? "—" },
                  { label: "TCP Conns",      value: load.tcpConnections ?? "—" },
                  { label: "HTTP Conns",     value: load.httpConnections ?? "—" },
                  { label: "Mem Resident",   value: fmtMB(ram.memoryResidentMB) },
                  { label: "Mem Tracking",   value: fmtMB(ram.memoryTrackingMB) },
                ]}
              />
            );
          })()}

          {/* RabbitMQ */}
          {(() => {
            const svc   = services.rabbitmq || {};
            const msgs  = svc.messages     || {};
            const rates = svc.messageRates || {};
            const node  = svc.node         || {};
            const nRam  = node.ram         || {};
            const ramPct = parseFloat(nRam.limitMB) ? (parseFloat(nRam.usedMB) / parseFloat(nRam.limitMB)) * 100 : 0;
            return (
              <ServiceCard
                name="RabbitMQ"
                service={services.rabbitmq}
                bars={[
                  { label: "Node RAM", pct: ramPct, meta: `${fmtMB(nRam.usedMB)} / ${fmtMB(nRam.limitMB)}` },
                ]}
                rows={[
                  { label: "Queues",     value: svc.queues ?? "—" },
                  { label: "Consumers",  value: svc.consumers ?? "—" },
                  { label: "Channels",   value: svc.channels ?? "—" },
                  { label: "Msgs Total", value: msgs.total ?? "—" },
                  { label: "Ready",      value: msgs.ready ?? "—" },
                  { label: "Unacked",    value: msgs.unacked ?? "—" },
                  { label: "Pub/s",      value: rates.publishPerSec ?? "—" },
                  { label: "Del/s",      value: rates.deliverPerSec ?? "—" },
                  { label: "Ack/s",      value: rates.ackPerSec ?? "—" },
                ]}
              />
            );
          })()}

          {/* Redis */}
          {(() => {
            const svc  = services.redis || {};
            const load = svc.load || {};
            const ram  = svc.ram  || {};
            const hits   = load.keyspaceHits   || 0;
            const misses = load.keyspaceMisses || 0;
            const total  = hits + misses;
            const hitPct = total > 0 ? (hits / total) * 100 : 0;
            const memPct = parseFloat(ram.peakMemoryMB) ? (parseFloat(ram.usedMemoryMB) / parseFloat(ram.peakMemoryMB)) * 100 : 0;
            return (
              <ServiceCard
                name="Redis"
                service={services.redis}
                bars={[
                  { label: "Memory",   pct: memPct, meta: `${fmtMB(ram.usedMemoryMB)} / ${fmtMB(ram.peakMemoryMB)} peak` },
                  { label: "Hit Rate", pct: hitPct, meta: `${hitPct.toFixed(1)}%`, color: hitPct >= 60 ? "#10b981" : hitPct >= 40 ? "#f59e0b" : "#f43f5e" },
                ]}
                rows={[
                  { label: "Version",  value: svc.version ? `v${svc.version}` : "—" },
                  { label: "Uptime",   value: formatUptime(svc.uptimeSeconds || 0) },
                  { label: "Clients",  value: load.connectedClients ?? "—" },
                  { label: "Ops/s",    value: load.opsPerSec ?? "—" },
                  { label: "Hits",     value: hits.toLocaleString() },
                  { label: "Misses",   value: misses.toLocaleString() },
                ]}
              />
            );
          })()}

        </div>
      </div>
    </div>
  );
};

export default SystemMonitor;
